import React, { FunctionComponent, useEffect } from 'react';

import CN from 'clsx';
import ReactModal from 'react-modal';

import { CloseButton } from '@r-client/shared/ui/core';
import { getDocument } from '@r-client/shared/util/core';

import styles from './modal.module.scss';

export interface IModalProps {
  id?: string;
  className?: string;
  closeModal?(
    event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
  ): void;
  isModalOpen: boolean;
  width?: 'default' | 'wide' | 'narrow' | 'medium';
  fixedWidth?: boolean;
  hideCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  smallVerticalPadding?: boolean;
  forVideo?: boolean;
}

export const Modal: FunctionComponent<React.PropsWithChildren<IModalProps>> = ({
  id,
  children,
  className,
  closeModal,
  isModalOpen = false,
  width,
  fixedWidth,
  hideCloseButton = false,
  closeOnOverlayClick = true,
  smallVerticalPadding,
  forVideo,
}) => {
  useEffect(() => {
    const body = getDocument()?.body;
    if (body) {
      ReactModal.setAppElement(body);
      ReactModal.defaultStyles = {};
    }
  }, []);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <ReactModal
      id={id}
      className={CN(className, styles.modal, {
        [styles.wide]: width === 'wide',
        [styles.medium]: width === 'medium',
        [styles.narrow]: width === 'narrow',
        [styles.fixedWidth]: fixedWidth,
        [styles.smallVerticalPadding]: smallVerticalPadding,
        [styles.forVideo]: forVideo,
      })}
      overlayClassName={styles.overlay}
      closeTimeoutMS={300}
      isOpen={isModalOpen}
      shouldCloseOnOverlayClick={closeOnOverlayClick}
      onRequestClose={closeModal}
      // react-modal requires root element to be set for modals: this is an a11y requirement.
      // https://stackoverflow.com/questions/48269381/warning-react-modal-app-element-is-not-defined-please-use-modal-setappeleme
      // eslint-disable-next-line no-process-env
      ariaHideApp={process.env.NODE_ENV !== 'test'}
    >
      {!hideCloseButton && (
        <CloseButton
          className={styles.closeBtn}
          role="button"
          dataTestId="modal-close"
          onClick={closeModal}
        />
      )}
      {children}
    </ReactModal>
  );
};

Modal.displayName = 'Modal';
